import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { Waypoint } from 'react-waypoint';
import Button from './Button';
import * as styles from './InfiniteScrollWaypoint.module.scss';
import LoadingIndicator from './LoadingIndicator';

export type FetchMoreHandler = () => Promise<unknown> | void;

interface InfiniteScrollWaypointProps {
  fetchMore?: FetchMoreHandler | null;
  maxConsecutivePage?: number;
}

const InfiniteScrollWaypoint = ({
  fetchMore,
  maxConsecutivePage = 10,
}: InfiniteScrollWaypointProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [consecutivePageLoaded, setConsecutivePageLoaded] = useState(0);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [stopLoading, setStopLoading] = useState(false);

  const handleEnter = ({ currentPosition }: Waypoint.CallbackArgs) => {
    if (
      fetchMore &&
      !isLoading &&
      !stopLoading &&
      currentPosition === 'inside'
    ) {
      const promise = Promise.resolve(fetchMore());
      setConsecutivePageLoaded((p) => p + 1);

      promise
        .catch(() => setStopLoading(true))
        .finally(() => setIsLoading(false));

      // We need to set flushSync here, because since automatic batching,
      // it can happen (for example in test code) that the promise either resolves immediately
      // or that the promise resolves in a setTimeout with 0 delay. In both these cases,
      // the call setIsLoading(true) bellow will be batched with setIsLoading(false)
      // above in the finally call. However, the components logic expects that `isLoading`
      // is true at least once when the promise is resolved, because it will cause Waypoint
      // to be unmounted and then remounted again when `isLoading` is false again.
      // Since this behaviour is specific to this component, instead of changing all test code
      // to use setTimeout with delay 1 everywhere, we decided to use flushSync here.
      flushSync(() => {
        setIsLoading(true);
      });
    }
  };

  if (!fetchMore || stopLoading) return null;

  return consecutivePageLoaded < maxConsecutivePage ? (
    <>
      {isLoading ? (
        <div className={styles.loader}>
          <LoadingIndicator delay={0} />
        </div>
      ) : (
        <Waypoint onEnter={handleEnter} />
      )}
    </>
  ) : (
    <div className={styles.loader}>
      <Button block variant="link" onClick={() => setConsecutivePageLoaded(0)}>
        Load more
      </Button>
    </div>
  );
};

export default InfiniteScrollWaypoint;
